/**
 * UnmatchedTagsCard — shows EPCs read by this smartbox's latest scan
 * that have NO mapping in `aws_smartbox3_mirror.md_rfid` (and therefore
 * don't contribute to the inventory / refill / dashboard totals).
 *
 * Surfaces the count immediately; expanding reveals the list. The
 * backend caps the list at 500 EPCs (`_UNMATCHED_CAP` in
 * inventory_db.py); when the cap fires, `unmatched_count` keeps the
 * true total while `unmatched.length` saturates at 500.
 *
 * When the parent page already has the per-box inventory loaded
 * (InventoryDetailPage), pass `unmatched` + `unmatchedCount` directly
 * and the card skips its own fetch. Without those props it self-
 * fetches via `/api/boxes/{scu}/inventory` — that's the SignalDetail
 * path, where the page only loads scan detail separately.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import {
  fetchInventory,
  InventoryApiError,
  type SmartboxInventory,
} from "../../services/inventoryApi";

interface UnmatchedTagsCardProps {
  scu: string;
  /** Pre-loaded unmatched payload. Provide both fields together; when
   *  either is undefined the card self-fetches. */
  unmatched?: string[];
  unmatchedCount?: number;
}

export const UnmatchedTagsCard: React.FC<UnmatchedTagsCardProps> = ({
  scu,
  unmatched: preUnmatched,
  unmatchedCount: preCount,
}) => {
  const { token, can } = useAuth();
  const havePreloaded =
    preUnmatched !== undefined && preCount !== undefined;

  const [inv, setInv] = useState<SmartboxInventory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (havePreloaded || !token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchInventory(scu, token)
      .then((d) => {
        if (!cancelled) setInv(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof InventoryApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "unknown error",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scu, token, havePreloaded]);

  const count = havePreloaded ? (preCount ?? 0) : (inv?.unmatched_count ?? 0);
  const list = havePreloaded ? (preUnmatched ?? []) : (inv?.unmatched ?? []);
  const ready = havePreloaded || inv !== null;
  const capped = count > list.length;
  const canMatch = can("tags.manage");

  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
          Unmatched tags
        </h2>
        {count > 0 ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-primary-700 hover:text-primary-900 hover:underline"
            aria-expanded={open}
          >
            {open ? "Hide list ▴" : `Show list ▾`}
          </button>
        ) : null}
      </div>

      {loading && !ready && (
        <div className="text-xs text-secondary-500">loading…</div>
      )}
      {error && (
        <div className="text-xs text-error-700 bg-error-50 px-2 py-1 rounded">
          Couldn't load inventory: {error}
        </div>
      )}

      {ready && (
        <>
          <div className="text-sm text-secondary-700">
            <strong className="text-secondary-900">{count}</strong> EPC
            {count === 1 ? "" : "s"} in this box's latest scan
            {count > 0 ? " have no catalog mapping" : " were unmatched"}.
            {count > 0 && (
              <span className="text-xs text-secondary-500 ml-1">
                (excluded from inventory + refill totals)
              </span>
            )}
          </div>

          {open && count > 0 && (
            <>
              <ul className="mt-2 max-h-72 overflow-auto border border-secondary-100 rounded divide-y divide-secondary-100">
                {list.map((epc) => (
                  <li
                    key={epc}
                    className="px-2 py-1 text-xs font-mono text-secondary-800 hover:bg-secondary-50"
                  >
                    {epc}
                  </li>
                ))}
              </ul>
              {capped && (
                <div className="text-xs text-secondary-500 mt-1 italic">
                  Showing the first {list.length} of {count}. The remaining{" "}
                  {count - list.length} aren't listed (server cap).
                </div>
              )}
              {canMatch && (
                <div className="mt-3 text-xs">
                  <Link
                    to="/tags/match"
                    className="text-primary-700 hover:text-primary-900 hover:underline"
                  >
                    → Match these tags to a product
                  </Link>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default UnmatchedTagsCard;
